<template>
  <date-picker
    :value="value"
    :type="type"
    :format="format"
    :placeholder="placeholder"
    :disabled="disabled"
    :disabled-date="disabledDateFn"
    :clearable="clearable"
    :class="pickerClasses"
    :input-class="inputClasses"
    :popup-class="popupClasses"
    :input-attr="inputAttributes"
    lang="en"
    v-bind="$attrs"
    @input="handleInput"
    @blur="$emit('blur', $event)"
    @focus="$emit('focus', $event)"
  >
    <template #calendar-icon>
      <theme-icon icon="calendar" type="icons" classes="w-4 h-4" />
    </template>
  </date-picker>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/lib/datepicker.css';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'ThemedDatePicker',
  components: {
    DatePicker,
    ThemeIcon
  },
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      default: null
    },
    value: {
      type: [String, Date, Array],
      default: null
    },
    type: {
      type: String,
      default: 'date',
      validator: (value) => ['date', 'time', 'datetime', 'daterange'].includes(value)
    },
    format: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    minDate: {
      type: Date,
      default: null
    },
    maxDate: {
      type: Date,
      default: null
    },
    clearable: {
      type: Boolean,
      default: true
    },
    error: {
      type: Boolean,
      default: false
    },
    ariaInvalid: {
      type: String,
      default: null
    },
    ariaDescribedby: {
      type: String,
      default: null
    },
    ariaRequired: {
      type: String,
      default: null
    }
  },
  computed: {
    pickerClasses() {
      return 'themed-date-picker';
    },
    inputClasses() {
      const baseClasses = 'themed-date-picker__input flex h-9 w-full rounded-md border px-3 py-1 text-sm transition-all outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50';
      
      const stateClasses = [];
      
      // Background and border
      stateClasses.push('bg-input-background border-input');
      
      // Focus states
      if (!this.error) {
        stateClasses.push('focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50');
      }
      
      // Error state
      if (this.error) {
        stateClasses.push('border-destructive focus-visible:border-destructive focus-visible:ring-2 focus-visible:ring-destructive/20');
      }
      
      return this.cn(baseClasses, ...stateClasses);
    },
    popupClasses() {
      return 'themed-date-picker__popup';
    },
    inputAttributes() {
      const attrs = {};
      if (this.id) attrs.id = this.id;
      if (this.ariaInvalid) attrs['aria-invalid'] = this.ariaInvalid;
      if (this.ariaDescribedby) attrs['aria-describedby'] = this.ariaDescribedby;
      if (this.ariaRequired) attrs['aria-required'] = this.ariaRequired;
      return attrs;
    },
    disabledDateFn() {
      if (!this.minDate && !this.maxDate) {
        return null;
      }
      
      return (date) => {
        if (this.minDate && date < this.minDate) {
          return true;
        }
        if (this.maxDate && date > this.maxDate) {
          return true;
        }
        return false;
      };
    }
  },
  methods: {
    handleInput(value) {
      this.$emit('input', value);
    }
  }
}
</script>
